/**
 * Copyright (C) Mairistem
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 *
 * Proprietary and confidential
 */

import _ from 'lodash';
import React from 'react';

import { Form, Grid } from '@mairistem/composants';
import { ParentForm, TypeComponent } from '@mairistem/administres-utils';

import { TypeDateNaissance } from '@/enums';
import { formatPrenomSaisie, resolveSexe, sexeToLibelle } from '@/utils';
import { composerNaissance, decomposerNaissance } from '@/utils/dateNaissance';

import PanelModalEdition from '../../PanelModalEdition';
import CommuneDelegueField from '../CommuneDelegueField';

interface InformationsParentModalProps {
  parent: any;
  numeroParent: number;
  header: string;
  onSave: () => Promise<void>;
  onChange: (data: Record<string, unknown>) => void;
  onClose: () => void;
  onCancel: () => void;
}

const InformationsParentModal = (props: InformationsParentModalProps) => {
  const { parent, numeroParent, header, onSave, onChange, onClose, onCancel } =
    props;

  const [loading, setLoading] = React.useState<boolean>(false);
  const [errors, setErrors] = React.useState<
    Record<string, string | boolean> & { api?: boolean }
  >({});

  const namePrefix = `parent${numeroParent}.`;

  // ParentForm attend un individu au format Individu (sexe, jour/mois/année de
  // naissance séparés, adresseNaissance avec des clés libelle*) alors que le
  // parent de la convention est plat (sexeCode, naissanceDate ISO,
  // naissanceCodePostal...) : on adapte dans les deux sens.
  const individuParent = React.useMemo(() => {
    return {
      sexe: resolveSexe(parent?.sexe, parent?.sexeCode) ?? undefined,
      nomFamille: parent?.nomFamille,
      doubleNom: parent?.doubleNom,
      nomPartieUn: parent?.nomPartieUn,
      nomPartieDeux: parent?.nomPartieDeux,
      prenom: parent?.prenom,
      prenomsComplementaires: parent?.prenomsComplementaires,
      // Précision enregistrée (jour ou mois inconnus) : mode C, M ou A
      ...decomposerNaissance(parent?.naissanceDate, parent?.naissanceTypeDate),
      adresseNaissance: {
        libelleCodePostal: parent?.naissanceCodePostal,
        libelleCommune: parent?.naissanceLocalite,
        libellePays: parent?.naissancePays,
        libelleDepartement: parent?.naissanceDepartementLibelle,
        codeDepartement: parent?.naissanceDepartementCode,
      },
    };
  }, [parent]);

  // DateNaissanceInput envoie jour/mois/année via 3 appels onChange distincts
  // pour une même sélection : on les accumule dans une ref pour recomposer la
  // date complète sans écraser les valeurs déjà saisies dans ce même batch.
  const naissanceDateRef = React.useRef<{
    jour: number | null;
    mois: number | null;
    annee: number | null;
    type: TypeDateNaissance;
  }>({ jour: null, mois: null, annee: null, type: TypeDateNaissance.COMPLET });

  React.useEffect(() => {
    const naissance = decomposerNaissance(
      parent?.naissanceDate,
      parent?.naissanceTypeDate,
    );
    naissanceDateRef.current = {
      jour: naissance.jourNaissance ?? null,
      mois: naissance.moisNaissance ?? null,
      annee: naissance.anneeNaissance ?? null,
      type: naissance.typeDateNaissance,
    };
  }, [parent?.naissanceDate, parent?.naissanceTypeDate]);

  const handleChangeDateNaissancePart = React.useCallback(
    (field: 'jour' | 'mois' | 'annee' | 'type', value: any) => {
      naissanceDateRef.current = {
        ...naissanceDateRef.current,
        [field]: value,
      };
      const { jour, mois, annee, type } = naissanceDateRef.current;
      // Date (partie inconnue au 1er) et sa précision, enregistrée par l'API
      onChange(composerNaissance(jour, mois, annee, type));
    },
    [onChange],
  );

  const handleChangeParent = React.useCallback(
    (_e: React.SyntheticEvent | null, data: { name: string; value: any }) => {
      if (!data?.name) return;

      // Effet interne de ParentForm : réinitialisation de l'objet parent quand
      // la filiation est inconnue. Non utilisé ici (filiation toujours connue).
      if (data.name === `parent${numeroParent}`) return;

      const field = data.name.startsWith(namePrefix)
        ? data.name.slice(namePrefix.length)
        : data.name;

      switch (field) {
        case 'sexe':
          onChange({
            sexe: data.value ?? null,
            sexeCode: sexeToLibelle(data.value),
          });
          break;
        case 'jourNaissance':
          handleChangeDateNaissancePart('jour', data.value);
          break;
        case 'moisNaissance':
          handleChangeDateNaissancePart('mois', data.value);
          break;
        case 'anneeNaissance':
          handleChangeDateNaissancePart('annee', data.value);
          break;
        case 'adresseNaissance':
          onChange({
            ...(!_.isNil(data.value?.libelleCodePostal) && {
              naissanceCodePostal: data.value.libelleCodePostal,
            }),
            ...(!_.isNil(data.value?.libelleCommune) && {
              naissanceLocalite: data.value.libelleCommune,
            }),
            ...(!_.isNil(data.value?.libellePays) && {
              naissancePays: data.value.libellePays,
            }),
            ...(!_.isNil(data.value?.libelleDepartement) && {
              naissanceDepartementLibelle: data.value.libelleDepartement,
            }),
            ...(!_.isNil(data.value?.codeDepartement) && {
              naissanceDepartementCode: data.value.codeDepartement,
            }),
          });
          break;
        case 'typeDateNaissance':
          handleChangeDateNaissancePart('type', data.value);
          break;
        // Non persisté dans la structure plate du parent.
        case 'identifiantNationalite':
          break;
        default:
          onChange({ [field]: formatPrenomSaisie(field, data.value) });
      }
    },
    [namePrefix, numeroParent, onChange, handleChangeDateNaissancePart],
  );

  const handleChangeCommuneDelegue = React.useCallback(
    (libelle: string, drapeau: boolean) => {
      onChange({
        naissanceLibelleCommuneDelegue: libelle,
        naissanceCommuneDelegue: drapeau,
      });
    },
    [onChange],
  );

  const handleSave = React.useCallback(() => {
    setLoading(true);

    onSave()
      .then(() => {
        onClose();
      })
      .catch(() => {
        setErrors({ api: true });
      })
      .finally(() => {
        setLoading(false);
      });
  }, [onClose, onSave]);

  return (
    <PanelModalEdition
      data-testid="parent-modal"
      errors={errors}
      loading={loading}
      header={header}
      onSave={handleSave}
      onClose={onCancel}
    >
      <Form>
        <Grid>
          {/* ParentForm se termine déjà par 1em de marge sous le lieu de
              naissance : avec le padding haut de la ligne suivante, on retrouve
              les 2em qui séparent les lignes du lieu de naissance. */}
          <Grid.Row style={{ paddingBottom: 0 }}>
            <Grid.Column width={16}>
              <ParentForm
                numeroParent={numeroParent}
                parent={individuParent}
                filiationInconnue={false}
                errors={{}}
                typeComponent={TypeComponent.PACS}
                onChange={handleChangeParent}
              />
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column
              width={16}
              style={{ paddingLeft: '2rem', paddingRight: '2rem' }}
            >
              <CommuneDelegueField
                data-testid="parent-commune-delegue"
                libelle={parent?.naissanceLibelleCommuneDelegue}
                drapeau={parent?.naissanceCommuneDelegue}
                onChange={handleChangeCommuneDelegue}
              />
            </Grid.Column>
          </Grid.Row>
        </Grid>
      </Form>
    </PanelModalEdition>
  );
};

export default InformationsParentModal;
